import { Component } from '@theme/component';
import { oncePerEditorSession } from '@theme/utilities';

/**
 * Comparison slider component for comparing two images
 *
 * @typedef {object} ComparisonSliderRefs
 * @property {HTMLElement} mediaWrapper - The container for the images
 * @property {HTMLInputElement} slider - The range input element
 * @property {HTMLElement} afterImage - The image that gets revealed
 *
 * @extends {Component<ComparisonSliderRefs>}
 *
 * @property {string[]} requiredRefs - Required refs: 'mediaWrapper', 'slider', and 'afterImage'
 */
export class ComparisonSliderComponent extends Component {
  requiredRefs = ['mediaWrapper', 'slider', 'afterImage'];

  constructor() {
    super();
    this.hasAnimated = false;
    this.isAnimating = false;
    this.autoplayRaf = null;
    /** @type {number[]} */
    this.animationTimeouts = [];
    this.isInViewport = false;
    this.autoplayMin = 35;
    this.autoplayMax = 65;
    this.autoplayDuration = 2600;
    this.autoplayPause = 700;
    this.prefersReducedMotion = false;
    this.followMouseEnabled = false;
    this.followRaf = null;
    this.followTargetCompare = 50;
    this.followCurrentCompare = 50;
    this.followEasing = 0.12;
    this.boundHandleIntersection = this.handleIntersection.bind(this);
    this.boundPauseAutoplay = this.pauseAutoplay.bind(this);
    this.boundResumeAutoplay = this.resumeAutoplay.bind(this);
    this.boundHandlePointerMove = this.handlePointerMove.bind(this);
    this.deferredRevealDelay = 900;
    this.waitingForAfterImage = false;
    this.initialCompareValue = 50;
  }

  /**
   * Called when component is added to DOM
   */
  connectedCallback() {
    super.connectedCallback();

    const { mediaWrapper, slider } = this.refs;

    // Get orientation from media wrapper
    this.orientation = mediaWrapper.dataset.orientation || 'horizontal';
    this.followMouseEnabled = mediaWrapper.dataset.followMouse === 'true';
    this.deferredRevealEnabled = mediaWrapper.dataset.deferReveal === 'true';
    this.deferredRevealDelay = this.getClampedValue(mediaWrapper.dataset.revealDelay, 900, 0, 4000);
    const followSpeedPercent = this.getClampedValue(mediaWrapper.dataset.followSpeed, 12, 2, 40);
    this.followEasing = this.getClampedValue(mediaWrapper.dataset.followEasing, followSpeedPercent / 100, 0.02, 0.6);
    this.autoplayEnabled = mediaWrapper.dataset.autoplay === 'true';

    if (this.followMouseEnabled) {
      this.autoplayEnabled = false;
    }

    this.autoplayMin = this.getClampedValue(mediaWrapper.dataset.autoplayMin, 35, 0, 100);
    this.autoplayMax = this.getClampedValue(mediaWrapper.dataset.autoplayMax, 65, 0, 100);
    this.autoplayDuration = this.getClampedValue(mediaWrapper.dataset.autoplayDuration, 2600, 600, 10000);
    this.autoplayPause = this.getClampedValue(mediaWrapper.dataset.autoplayPause, 700, 0, 5000);
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.autoplayMin > this.autoplayMax) {
      [this.autoplayMin, this.autoplayMax] = [this.autoplayMax, this.autoplayMin];
    }

    // Initialize the position. If requested, defer the split reveal until the second image is ready.
    this.initialCompareValue = this.getClampedValue(slider.value, 50, 0, 100);
    const didStartDeferredReveal = this.startDeferredReveal();
    if (!didStartDeferredReveal) {
      // If the after image was already cached, animate the initial reveal smoothly
      const currentCompare = mediaWrapper.style.getPropertyValue('--compare');
      if (currentCompare === '100') {
        mediaWrapper.style.setProperty('--transition-duration', '0.6s');
        this.sync();
        setTimeout(() => {
          mediaWrapper.style.setProperty('--transition-duration', '0s');
        }, 700);
      } else {
        this.sync();
      }
    }

    if (this.followMouseEnabled) {
      this.followCurrentCompare = this.getClampedValue(slider.value, 50, 0, 100);
      this.followTargetCompare = this.followCurrentCompare;
      mediaWrapper.addEventListener('pointermove', this.boundHandlePointerMove);
      mediaWrapper.addEventListener('pointerdown', this.boundHandlePointerMove);
      this.startFollowAnimation();
    }

    if (this.autoplayEnabled && !this.prefersReducedMotion) {
      slider.addEventListener('pointerdown', this.boundPauseAutoplay);
      slider.addEventListener('pointerup', this.boundResumeAutoplay);
      slider.addEventListener('pointercancel', this.boundResumeAutoplay);
      slider.addEventListener('change', this.boundResumeAutoplay);

      this.startAutoplay();
    }

    // Set up intersection observer for animation
    this.setupIntersectionObserver();
  }

  /**
   * Get the nested image inside the after layer
   * @returns {HTMLImageElement | null}
   */
  getAfterImageElement() {
    const { afterImage } = this.refs;
    if (!afterImage) return null;

    return afterImage.querySelector('img');
  }

  /**
   * Hold the slider at 100% until the after image has loaded, then reveal the configured split.
   * @returns {boolean}
   */
  startDeferredReveal() {
    if (!this.deferredRevealEnabled) return false;

    const afterImageElement = this.getAfterImageElement();
    if (!afterImageElement) return false;

    // If image is already fully loaded and decoded, skip deferral
    if (afterImageElement.complete && afterImageElement.naturalWidth > 0) return false;

    const { mediaWrapper, slider } = this.refs;

    this.waitingForAfterImage = true;
    slider.value = '100';
    mediaWrapper.style.setProperty('--compare', '100');

    let didResolve = false;

    const finishDeferredReveal = async () => {
      if (didResolve) return;
      didResolve = true;

      // Wait for the browser to fully decode the image before revealing
      try {
        await afterImageElement.decode();
      } catch {
        // decode() can fail on broken images — proceed anyway
      }

      this.animationTimeouts.push(
        setTimeout(() => {
          if (!this.isConnected) return;

          this.waitingForAfterImage = false;

          // Smooth transition from 100 → initial value to prevent black flash
          mediaWrapper.style.setProperty('--transition-duration', '0.6s');
          this.setValue(this.initialCompareValue);

          if (this.followMouseEnabled) {
            this.followCurrentCompare = this.initialCompareValue;
            this.followTargetCompare = this.initialCompareValue;
          }

          // Clear transition after it completes, then trigger intro animation
          this.animationTimeouts.push(
            setTimeout(() => {
              mediaWrapper.style.setProperty('--transition-duration', '0s');
              if (this.isInViewport && !this.hasAnimated) {
                this.triggerIntroAnimation();
              }
            }, 650)
          );
        }, this.deferredRevealDelay)
      );
    };

    afterImageElement.addEventListener('load', finishDeferredReveal, { once: true });
    afterImageElement.addEventListener('error', finishDeferredReveal, { once: true });

    // Guard against race: image may have completed between our check and listener attachment
    if (afterImageElement.complete && afterImageElement.naturalWidth > 0) {
      finishDeferredReveal();
    }

    return true;
  }

  /**
   * Trigger the one-time intro animation when the component is ready.
   */
  triggerIntroAnimation() {
    this.animationTimeouts.push(
      setTimeout(() => {
        oncePerEditorSession(this, `comparison-slider-animated`, () => {
          this.animateSlider();
        });
      }, 300)
    );
  }

  /**
   * Parse and clamp dataset values
   * @param {string | undefined} value
   * @param {number} fallback
   * @param {number} min
   * @param {number} max
   * @returns {number}
   */
  getClampedValue(value, fallback, min, max) {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return fallback;

    return Math.min(Math.max(parsed, min), max);
  }

  /**
   * Sync the CSS custom property with the input value
   */
  sync() {
    const { mediaWrapper, slider } = this.refs;
    // Skip sync during animation to prevent lag
    if (this.isAnimating) return;

    const val = (Number(slider.value) - Number(slider.min)) / (Number(slider.max) - Number(slider.min));
    const compareValue = Math.round(val * 100);

    // Set the CSS custom property on the media wrapper
    mediaWrapper.style.setProperty('--compare', String(compareValue));
  }

  /**
   * Clean up when component is removed
   */
  disconnectedCallback() {
    const { slider, mediaWrapper } = this.refs;

    if (slider) {
      slider.removeEventListener('pointerdown', this.boundPauseAutoplay);
      slider.removeEventListener('pointerup', this.boundResumeAutoplay);
      slider.removeEventListener('pointercancel', this.boundResumeAutoplay);
      slider.removeEventListener('change', this.boundResumeAutoplay);
    }

    if (mediaWrapper) {
      mediaWrapper.removeEventListener('pointermove', this.boundHandlePointerMove);
      mediaWrapper.removeEventListener('pointerdown', this.boundHandlePointerMove);
    }

    this.stopAutoplay();
    this.stopFollowAnimation();
    this.clearAnimationTimeouts();

    // Clean up intersection observer
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  /**
   * Set the slider value and update display
   * @param {number} value - Value between 0-100 (0 = all after, 100 = all before)
   */
  setValue(value) {
    const { slider } = this.refs;
    if (!slider) return;

    slider.value = String(value);
    this.sync();
  }

  /**
   * Animate the slider handle to give users a hint about the interaction
   */
  animateSlider() {
    const { mediaWrapper, slider } = this.refs;
    if (this.hasAnimated) return;

    this.hasAnimated = true;
    this.isAnimating = true;

    // Enable transition for smooth animation
    mediaWrapper.style.setProperty('--transition-duration', '0.5s');

    // Create a subtle sliding animation by only setting CSS property
    this.animationTimeouts.push(
      setTimeout(() => {
        mediaWrapper.style.setProperty('--compare', '40');
      }, 100)
    );

    this.animationTimeouts.push(
      setTimeout(() => {
        mediaWrapper.style.setProperty('--compare', '60');
      }, 600)
    );

    this.animationTimeouts.push(
      setTimeout(() => {
        mediaWrapper.style.setProperty('--compare', '50');
      }, 1100)
    );

    this.animationTimeouts.push(
      setTimeout(() => {
        // Remove transition after animation and sync slider value
        mediaWrapper.style.setProperty('--transition-duration', '0s');
        // Sync the slider value to match the final position
        slider.value = '50';
        this.isAnimating = false;
      }, 1600)
    );
  }

  /**
   * Clear pending timeout handles
   */
  clearAnimationTimeouts() {
    this.animationTimeouts.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    this.animationTimeouts = [];
  }

  /**
   * Follow pointer position across the media wrapper
   * @param {PointerEvent} event
   */
  handlePointerMove(event) {
    if (!this.followMouseEnabled) return;

    const { mediaWrapper } = this.refs;
    if (!mediaWrapper) return;

    const rect = mediaWrapper.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;

    let percent = 50;

    if (this.orientation === 'vertical') {
      const relativeY = event.clientY - rect.top;
      percent = 100 - (relativeY / rect.height) * 100;
    } else {
      const relativeX = event.clientX - rect.left;
      percent = (relativeX / rect.width) * 100;
    }

    this.followTargetCompare = Math.min(Math.max(Math.round(percent), 0), 100);
  }

  /**
   * Start smooth follow animation loop
   */
  startFollowAnimation() {
    if (!this.followMouseEnabled || this.followRaf) return;

    const step = () => {
      const { mediaWrapper, slider } = this.refs;
      if (!mediaWrapper || !slider) {
        this.followRaf = null;
        return;
      }

      const delta = this.followTargetCompare - this.followCurrentCompare;
      if (Math.abs(delta) > 0.01) {
        this.followCurrentCompare += delta * this.followEasing;
      } else {
        this.followCurrentCompare = this.followTargetCompare;
      }

      const compareValue = Math.min(Math.max(Math.round(this.followCurrentCompare), 0), 100);
      slider.value = String(compareValue);
      mediaWrapper.style.setProperty('--compare', String(compareValue));

      this.followRaf = requestAnimationFrame(step);
    };

    this.followRaf = requestAnimationFrame(step);
  }

  /**
   * Stop smooth follow animation loop
   */
  stopFollowAnimation() {
    if (this.followRaf) {
      cancelAnimationFrame(this.followRaf);
      this.followRaf = null;
    }
  }

  /**
   * Pause autoplay while user interacts with the slider
   */
  pauseAutoplay() {
    this.stopAutoplay();
  }

  /**
   * Resume autoplay after interaction, if the component is visible
   */
  resumeAutoplay() {
    if (!this.autoplayEnabled || this.prefersReducedMotion || !this.isInViewport) return;
    this.startAutoplay();
  }

  /**
   * Start continuous speed-ramped autoplay
   */
  startAutoplay() {
    const { mediaWrapper, slider } = this.refs;
    if (!slider || !mediaWrapper || this.autoplayRaf) return;

    this.isAnimating = true;
    mediaWrapper.style.setProperty('--transition-duration', '0s');

    let direction = 1;
    let from = this.autoplayMin;
    let to = this.autoplayMax;
    /** @type {number | null} */
    let startTime = null;
    /** @type {number | null} */
    let pauseUntil = null;

    const easeInOutQuad = (/** @type {number} */ progress) => {
      return progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2;
    };

    const step = (/** @type {number} */ timestamp) => {
      if (!this.isAnimating) return;

      if (pauseUntil && timestamp < pauseUntil) {
        this.autoplayRaf = requestAnimationFrame(step);
        return;
      }

      if (pauseUntil && timestamp >= pauseUntil) {
        pauseUntil = null;
        startTime = timestamp;
      }

      if (startTime === null) startTime = timestamp;

      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / this.autoplayDuration, 1);
      const value = from + (to - from) * easeInOutQuad(progress);
      const compareValue = Math.round(value);

      slider.value = String(compareValue);
      mediaWrapper.style.setProperty('--compare', String(compareValue));

      if (progress >= 1) {
        direction *= -1;
        from = direction === 1 ? this.autoplayMin : this.autoplayMax;
        to = direction === 1 ? this.autoplayMax : this.autoplayMin;
        startTime = timestamp;
        pauseUntil = timestamp + this.autoplayPause;
      }

      this.autoplayRaf = requestAnimationFrame(step);
    };

    this.autoplayRaf = requestAnimationFrame(step);
  }

  /**
   * Stop autoplay and keep current slider position
   */
  stopAutoplay() {
    if (this.autoplayRaf) {
      cancelAnimationFrame(this.autoplayRaf);
      this.autoplayRaf = null;
    }

    this.isAnimating = false;
  }

  /**
   * Set up intersection observer to detect when section comes into view
   */
  setupIntersectionObserver() {
    if (this.autoplayEnabled && !this.prefersReducedMotion) return;
    if (!window.IntersectionObserver) return;

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0,
    };

    this.intersectionObserver = new IntersectionObserver(this.boundHandleIntersection, options);
    this.intersectionObserver.observe(this);
  }

  /**
   * Handle intersection observer callback
   * @param {IntersectionObserverEntry[]} entries
   */
  handleIntersection(entries) {
    entries.forEach((entry) => {
      this.isInViewport = entry.isIntersecting;

      if (entry.isIntersecting && !this.hasAnimated && !this.waitingForAfterImage) {
        this.triggerIntroAnimation();

        // Disconnect observer after first animation
        if (this.intersectionObserver) {
          this.intersectionObserver.disconnect();
        }
      }
    });
  }
}

// Register the custom element
if (!customElements.get('comparison-slider-component')) {
  customElements.define('comparison-slider-component', ComparisonSliderComponent);
}
